/**
 * errorMessages.js — traduzione centralizzata degli errori del Booking/Membership Engine
 * in messaggi user-friendly in italiano.
 *
 * Il backend Booking Engine v1.0 restituisce `detail` strutturato per alcuni casi:
 * - `product_selection_required` (gestito nel frontend via ProductPicker, non serve messaggio)
 * - `no_valid_product`
 * - `capacity_below_booked`
 * - `sedi diverse` (string detail)
 * - `already_booked`
 * - etc.
 *
 * Questa mappa converte codici tecnici (o stringhe/detail objects) in copy elegante
 * "Boutique Pilates" adatta a uno stakeholder demo.
 */

const CODE_MAP = {
  // Booking Engine
  product_selection_required: "Seleziona con quale abbonamento vuoi prenotare.",
  no_valid_product: "Nessun abbonamento valido per questa lezione. Contatta la reception.",
  session_full: "La lezione è al completo. Puoi entrare in lista d'attesa.",
  already_booked: "Sei già prenotata a questa lezione.",
  session_not_found: "Lezione non trovata o non più disponibile.",
  booking_not_found: "Prenotazione non trovata.",
  outside_cancellation_window: "Cancellazione fuori tempo massimo. Contatta la reception.",
  cross_sede_not_allowed: "Non puoi spostare la prenotazione tra sedi diverse.",
  capacity_below_booked: "Ci sono più prenotazioni della nuova capienza richiesta. Conferma per procedere.",
  booking_not_confirmed: "Questa prenotazione non è nello stato 'confermato'.",
  wallet_exhausted: "Hai esaurito gli ingressi disponibili.",
  frozen_assignment: "L'abbonamento è sospeso.",
  expired_assignment: "L'abbonamento è scaduto.",
  cancelled_assignment: "L'abbonamento è stato annullato.",

  // Auth
  invalid_credentials: "Credenziali non valide.",
  totp_required: "Codice 2FA richiesto.",
  totp_invalid: "Codice 2FA non valido.",
  sede_disabled: "La tua sede è stata disattivata.",

  // Flag operativi (Instructor dashboard)
  manual_without_product: "Inserimento manuale senza abbonamento",
  manual_overbooking: "Overbooking manuale (fuori capienza)",
};

const STRING_MAP = [
  [/^sedi diverse/i, "Non puoi spostare la prenotazione tra sedi diverse."],
  [/^Cliente ha più prodotti validi/i, "Il cliente ha più abbonamenti validi. Seleziona quale usare."],
  [/^Cliente non ha prodotti validi/i, "Il cliente non ha abbonamenti validi per questa lezione."],
  [/Overbooking non consentito/i, "La lezione è al completo. Attiva l'override 'overbooking manuale' se vuoi comunque procedere."],
  [/^Prenotazione non trovata/i, "Prenotazione non trovata."],
  [/^Lezione non trovata/i, "Lezione non trovata."],
  [/^Credenziali non valide/i, "Credenziali non valide."],
  [/^Utente non trovato/i, "Utente non trovato."],
  [/^Non autenticato/i, "Sessione scaduta. Effettua di nuovo l'accesso."],
  [/^Token non valido o scaduto/i, "Sessione scaduta. Effettua di nuovo l'accesso."],
  [/^Permessi insufficienti/i, "Non hai i permessi per questa operazione."],
];

/**
 * Traduce un errore backend in messaggio user-friendly italiano.
 * Accetta:
 *  - Error di axios (con err.response.data.detail)
 *  - stringa
 *  - object con .detail
 *  - null/undefined → messaggio generico
 */
export function humanizeError(err) {
  if (!err) return "Si è verificato un errore. Riprova.";
  // axios error
  const detail = err?.response?.data?.detail ?? err;

  if (typeof detail === "string") {
    return mapString(detail);
  }
  if (Array.isArray(detail)) {
    return detail
      .map((e) => e?.msg || (typeof e === "string" ? e : JSON.stringify(e)))
      .map(mapString)
      .join(" · ");
  }
  if (typeof detail === "object" && detail !== null) {
    if (detail.error && CODE_MAP[detail.error]) return CODE_MAP[detail.error];
    if (detail.message) return mapString(detail.message);
    if (detail.msg) return mapString(detail.msg);
  }
  if (err?.message) return mapString(err.message);
  return "Si è verificato un errore. Riprova.";
}

function mapString(s) {
  if (!s || typeof s !== "string") return "Si è verificato un errore. Riprova.";
  for (const [re, msg] of STRING_MAP) {
    if (re.test(s)) return msg;
  }
  return s;
}

/**
 * Human label per flag "manual_*" — usato nelle etichette UI degli operatori.
 */
export function humanizeFlag(flag) {
  return CODE_MAP[flag] || flag;
}
